import { stampFootprint, type PropFootprint } from "../compile/footprint.ts";
import type { PropInstance, TileRef } from "../document/types.ts";
import type { GroundRule } from "./ground-at.ts";
import { pieceBase, type FrameArtInfo } from "./scatter-base.ts";
import { meanGapSquared } from "./scatter-density.ts";
import type { PropTemplate } from "./templates.ts";

/**
 * The prop brush's entries as the scatter reads them (ADR-123 decisions 1.3, 2.1 and 2.3), and the set prepared
 * once a stroke: each flip an entry may be drawn in, laid out as the cells it blocks and the pixels its pieces
 * cover, so a candidate is tested by array reads alone.
 */

/**
 * Which axes an entry may be mirrored on at random, each with even odds.
 */
export type BrushFlip = "none" | "x" | "y" | "xy";

/**
 * What an entry asks of the scatter: how often it is picked, the room it keeps in pixels, its random flips and its
 * ground rule.
 */
export interface EntryRule {
  readonly weight: number;
  readonly gap: number;
  readonly flip: BrushFlip;
  readonly ground?: GroundRule;
}

/**
 * One frame an entry places: its size and footprint, what the manifest says of its art (the base of a piece with
 * no footprint is a strip of it), its offset in whole pixels from the entry's anchor (0 for a frame entry), and its
 * own flips (a template's piece's; false for a frame entry).
 */
export interface ScatterPiece {
  readonly frame: TileRef;
  readonly width: number;
  readonly height: number;
  readonly footprint: PropFootprint | undefined;
  readonly art: FrameArtInfo | undefined;
  readonly dx: number;
  readonly dy: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
}

/**
 * A usable entry: a single frame, or a template's pieces placed whole.
 */
export interface ScatterEntry extends EntryRule {
  readonly kind: "frame" | "template";
  readonly pieces: readonly ScatterPiece[];
}

/**
 * What the editor knows of frames: a frame's size in pixels, the footprint a prop blocks by (its own when it
 * carries one, its frame's otherwise; `blockingFootprint`), and its art as the asset manifest holds it (`base`,
 * `art`, flat or empty), which gives a prop with no footprint its base (ADR-123's amendment of 2026-10-13, A1a.1);
 * undefined for a frame the manifest does not hold, which has only its foot cell.
 */
export interface FrameLookups {
  readonly frameSize: (frame: TileRef) => { readonly w: number; readonly h: number } | undefined;
  readonly footprintOf: (prop: PropInstance) => PropFootprint | undefined;
  readonly artOf: (frame: TileRef) => FrameArtInfo | undefined;
}

/**
 * Whether a set keeps its gap from props that are not its own (`keepsGapFromOthers`), whether it may stand on the
 * base of other props (`allowsPlacingOnTop`), whether it clears the known cover under what it places
 * (`clearsGroundCover`, which allowing placing on top overrides, A3.1), and the frames, by `frameKey`, that are known
 * ground cover (`knownCover`, ADR-123's amendment of 2026-10-13, A2); each off or empty when left out.
 */
export interface RoomOptions {
  readonly keepsGapFromOthers?: boolean;
  readonly allowsPlacingOnTop?: boolean;
  readonly clearsGroundCover?: boolean;
  readonly cover?: ReadonlySet<string>;
}

/**
 * One entry drawn in one flip. Offsets are in whole pixels from the anchor's top-left, `(x * tileSize, y *
 * tileSize)` of the placement. The lead piece (the anchor piece, at offset 0, else the first) stands for the entry:
 * its foot point is the one drawn on a cell corner and tested against the circle and the ground, and its laying
 * edge, `leadX` from the anchor, the one snapped to the cells. `pieces` holds six numbers a piece: the art's left,
 * top, right and bottom and the foot point's x and y. `gaps` holds each piece's gap. `blocks` holds the cells the
 * footprints block as column and row pairs from the lead's laying corner: the cell column of its laying edge and
 * the cell row of its bottom edge. `base` adds the foot cell of each solid piece. `foot` holds those foot cells alone,
 * and `cover` says the entry is a frame entry of known ground cover, which paints only `foot` into the occupancy
 * and is tested by its whole `base` against the bases of other props but never against other cover's foot cells.
 */
export interface ScatterShape {
  readonly flipX: boolean;
  readonly flipY: boolean;
  readonly leadX: number;
  readonly footX: number;
  readonly footY: number;
  readonly pieces: Int32Array;
  readonly gaps: Int32Array;
  readonly blocks: Int32Array;
  readonly base: Int32Array;
  readonly foot: Int32Array;
  readonly cover: boolean;
}

/**
 * A set prepared for a stroke: its usable entries, each one's shapes indexed by its flips (bit 0 mirrored, bit 1
 * upside down; undefined for a flip the entry does not allow), the weights' running sums, the mean squared gap, the
 * largest gap, the gap each of the set's frames keeps, how far any piece's art reaches from its entry's foot, and the
 * set's own ground rule, read before each entry's, its room options (clearing is on only when placing on top is off),
 * and the known cover it was prepared with.
 */
export interface ScatterKit {
  readonly entries: readonly ScatterEntry[];
  readonly shapes: readonly (readonly (ScatterShape | undefined)[])[];
  readonly cumulative: readonly number[];
  readonly totalWeight: number;
  readonly meanGapSq: number;
  readonly maxGap: number;
  readonly gaps: ReadonlyMap<string, number>;
  readonly tileSize: number;
  readonly cellsPerTile: number;
  readonly cellPx: number;
  readonly reach: number;
  readonly ground?: GroundRule;
  readonly keepsGapFromOthers: boolean;
  readonly allowsPlacingOnTop: boolean;
  readonly clearsGroundCover: boolean;
  readonly cover: ReadonlySet<string>;
}

/**
 * The largest weight an entry takes.
 */
const WEIGHT_MAX = 1000;

/**
 * The largest gap an entry keeps, in pixels.
 */
const GAP_MAX = 1024;

/**
 * A frame's key, flips ignored: the tile, then the tileset, so no tileset name can make two frames collide.
 *
 * @param frame The frame.
 * @returns The key.
 */
export function frameKey(frame: TileRef): string {
  return `${frame.tile}:${frame.tileset}`;
}

/**
 * A placed prop of a frame, at the origin, carrying a piece's own footprint when it has one: what `footprintOf`
 * reads.
 *
 * @param frame The frame.
 * @param own The piece's own footprint cells, if any.
 * @returns The prop.
 */
export function probe(frame: TileRef, own?: PropInstance["footprint"]): PropInstance {
  return { id: "", frame: { tileset: frame.tileset, tile: frame.tile }, x: 0, y: 0, flipX: false, flipY: false, sortPixels: 0, ...(own === undefined ? {} : { footprint: own }) };
}

/**
 * The rule's fields as an entry carries them.
 *
 * @param rule The rule.
 * @returns The fields, `ground` only when present.
 */
function ruleOf(rule: EntryRule): EntryRule {
  return { weight: rule.weight, gap: rule.gap, flip: rule.flip, ...(rule.ground === undefined ? {} : { ground: rule.ground }) };
}

/**
 * A frame entry as the scatter reads it: one piece at the anchor.
 *
 * @param frame The frame, flips ignored.
 * @param rule The entry's weight, gap, flip and ground.
 * @param lookups The editor's frame sizes and footprints.
 * @returns The entry, or null when the catalogue cannot size the frame, which leaves the entry unusable.
 */
export function frameScatterEntry(frame: TileRef, rule: EntryRule, lookups: FrameLookups): ScatterEntry | null {
  const size = lookups.frameSize(frame);
  if (size === undefined) {
    return null;
  }
  const piece: ScatterPiece = { frame: { tileset: frame.tileset, tile: frame.tile }, width: size.w, height: size.h, footprint: lookups.footprintOf(probe(frame)), art: lookups.artOf(frame), dx: 0, dy: 0, flipX: false, flipY: false };
  return { kind: "frame", ...ruleOf(rule), pieces: [piece] };
}

/**
 * A template entry as the scatter reads it: every piece at its offset, with its own flips and its own footprint,
 * placed whole (ADR-123 decision 1.3).
 *
 * @param template The template.
 * @param rule The entry's weight, gap, flip and ground.
 * @param lookups The editor's frame sizes and footprints.
 * @returns The entry, or null when it is not usable: the template has no pieces, a piece carries a behaviour (a
 *   brush never scatters a thing a player acts on), or the catalogue cannot size a piece's frame.
 */
export function templateScatterEntry(template: PropTemplate, rule: EntryRule, lookups: FrameLookups): ScatterEntry | null {
  if (template.pieces.length === 0 || template.pieces.some((piece) => piece.behaviour !== undefined)) {
    return null;
  }
  const pieces: ScatterPiece[] = [];
  for (const piece of template.pieces) {
    const size = lookups.frameSize(piece.frame);
    if (size === undefined) {
      return null;
    }
    pieces.push({ frame: { tileset: piece.frame.tileset, tile: piece.frame.tile }, width: size.w, height: size.h, footprint: lookups.footprintOf(probe(piece.frame, piece.footprint)), art: lookups.artOf(piece.frame), dx: piece.dx, dy: piece.dy, flipX: piece.flipX, flipY: piece.flipY });
  }
  return { kind: "template", ...ruleOf(rule), pieces };
}

/**
 * The index of an entry's lead piece: the first at offset 0, else the first.
 *
 * @param entry The entry.
 * @returns The index.
 */
function leadIndex(entry: ScatterEntry): number {
  const found = entry.pieces.findIndex((piece) => piece.dx === 0 && piece.dy === 0);
  return found === -1 ? 0 : found;
}

/**
 * The cells marked in a scratch grid as column and row pairs from the lead's laying corner.
 *
 * @param grid The grid, row by row.
 * @param across The grid's width in cells.
 * @param kx The lead's laying corner column.
 * @param ky The lead's laying corner row.
 * @returns The pairs, in reading order.
 */
function markedCells(grid: Uint8Array, across: number, kx: number, ky: number): Int32Array {
  const out: number[] = [];
  for (let i = 0; i < grid.length; i += 1) {
    if (grid[i] !== 0) {
      out.push((i % across) - kx, Math.floor(i / across) - ky);
    }
  }
  return Int32Array.from(out);
}

/**
 * Lays an entry out in one flip: its pieces' art and feet from the anchor, and the cells its pieces' footprints (or
 * art strips, for a frame with no footprint) block from the lead's laying corner, found by stamping every piece with
 * `stampFootprint` onto a scratch grid with the lead on a cell corner; the base adds each solid piece's foot cell.
 * Every candidate stands with the lead on a cell corner, and the stamp's rounding is the same at
 * every whole-cell shift, so these offsets are exactly what the stamp marks wherever the entry lands.
 *
 * @param entry The entry.
 * @param flipX Whether the entry is mirrored.
 * @param flipY Whether the entry is upside down.
 * @param gaps The gap each of the set's frames keeps.
 * @param tileSize Tile size in pixels.
 * @param cellsPerTile Authoring cells per tile.
 * @param cover Whether the entry is known ground cover.
 * @returns The shape.
 */
function shapeOf(entry: ScatterEntry, flipX: boolean, flipY: boolean, gaps: ReadonlyMap<string, number>, tileSize: number, cellsPerTile: number, cover: boolean): ScatterShape {
  const cell = tileSize / cellsPerTile;
  const lead = entry.pieces[leadIndex(entry)] as ScatterPiece;
  const leadX = lead.dx + (lead.flipX !== flipX ? lead.width : 0);
  const pieces = new Int32Array(entry.pieces.length * 6);
  const pieceGaps = new Int32Array(entry.pieces.length);
  let minLeft = 0;
  let minTop = 0;
  let maxRight = 0;
  let maxBottom = 0;
  entry.pieces.forEach((piece, i) => {
    const bottom = piece.dy + tileSize;
    pieces.set([piece.dx, bottom - piece.height, piece.dx + piece.width, bottom, piece.dx + Math.floor(piece.width / 2), bottom], i * 6);
    pieceGaps[i] = gaps.get(frameKey(piece.frame)) ?? 0;
    minLeft = Math.min(minLeft, piece.dx);
    minTop = Math.min(minTop, bottom - piece.height);
    maxRight = Math.max(maxRight, piece.dx + piece.width);
    maxBottom = Math.max(maxBottom, bottom);
  });
  const kx = Math.ceil((leadX - minLeft) / cell) + 1;
  const ky = Math.ceil((lead.dy + tileSize - minTop) / cell) + 1;
  const left = kx * cell - leadX;
  const top = ky * cell - (lead.dy + tileSize);
  const width = Math.ceil((left + maxRight) / tileSize) + 2;
  const height = Math.ceil((top + maxBottom) / tileSize) + 2;
  const grid = new Uint8Array(width * cellsPerTile * height * cellsPerTile);
  const solid: boolean[] = [];
  for (const piece of entry.pieces) {
    const base = pieceBase(piece.footprint, piece.art);
    solid.push(base.solid);
    if (base.footprint !== undefined) {
      stampFootprint(grid, width, height, base.footprint, { x: (left + piece.dx) / tileSize, y: (top + piece.dy) / tileSize, flipX: piece.flipX !== flipX, flipY: piece.flipY !== flipY }, tileSize, cellsPerTile, 1);
    }
  }
  const across = width * cellsPerTile;
  const blocks = markedCells(grid, across, kx, ky);
  const foot: number[] = [];
  entry.pieces.forEach((_, i) => {
    if (solid[i] === true) {
      const column = Math.floor((left + (pieces[i * 6 + 4] as number)) / cell);
      const row = Math.floor((top + (pieces[i * 6 + 5] as number) - 1) / cell);
      if (column >= 0 && row >= 0 && column < across && row < height * cellsPerTile) {
        grid[row * across + column] = 1;
        foot.push(column - kx, row - ky);
      }
    }
  });
  return { flipX, flipY, leadX, footX: lead.dx + Math.floor(lead.width / 2), footY: lead.dy + tileSize, pieces, gaps: pieceGaps, blocks, base: markedCells(grid, across, kx, ky), foot: Int32Array.from(foot), cover };
}

/**
 * Whether an entry may be drawn in a flip.
 *
 * @param flip The entry's allowed flips.
 * @param variant The flip: bit 0 mirrored, bit 1 upside down.
 * @returns True when allowed.
 */
function allows(flip: BrushFlip, variant: number): boolean {
  return ((variant & 1) === 0 || flip === "x" || flip === "xy") && ((variant & 2) === 0 || flip === "y" || flip === "xy");
}

/**
 * Prepares a set's usable entries for a stroke. A prop of the set keeps its frame's gap: a frame entry's, a
 * template piece's template's, the larger when a frame is both; the scatter reads that one gap for a frame wherever
 * it meets one, placed earlier or by hand, so every pair is judged alike.
 *
 * @param entries The usable entries, in the set's order.
 * @param tileSize Tile size in pixels.
 * @param cellsPerTile Authoring cells per tile.
 * @param ground The set's own ground rule, which every entry's foot point passes before the entry's own.
 * @param options The set's room options and the known cover, each off or empty when left out.
 * @returns The prepared set.
 * @throws Error when there is no entry, a weight is not a whole number from 1 to 1,000, a gap not a whole number
 *   from 0 to 1,024, an entry has no pieces, or a template entry is mirrored.
 */
export function prepareScatter(entries: readonly ScatterEntry[], tileSize: number, cellsPerTile: number, ground?: GroundRule, options: RoomOptions = {}): ScatterKit {
  if (entries.length === 0) {
    throw new Error("a brush needs at least one usable entry");
  }
  const gaps = new Map<string, number>();
  entries.forEach((entry, i) => {
    if (!Number.isInteger(entry.weight) || entry.weight < 1 || entry.weight > WEIGHT_MAX) {
      throw new Error(`entry ${i + 1}: the weight must be a whole number from 1 to ${WEIGHT_MAX}, received ${String(entry.weight)}`);
    }
    if (!Number.isInteger(entry.gap) || entry.gap < 0 || entry.gap > GAP_MAX) {
      throw new Error(`entry ${i + 1}: the gap must be a whole number of pixels from 0 to ${GAP_MAX}, received ${String(entry.gap)}`);
    }
    if (entry.pieces.length === 0) {
      throw new Error(`entry ${i + 1} has no pieces`);
    }
    if (entry.kind === "template" && entry.flip !== "none") {
      throw new Error(`entry ${i + 1}: a template is never mirrored, received flip ${entry.flip}`);
    }
    for (const piece of entry.pieces) {
      const key = frameKey(piece.frame);
      gaps.set(key, Math.max(gaps.get(key) ?? 0, entry.gap));
    }
  });
  const cellPx = tileSize / cellsPerTile;
  const cover = options.cover ?? new Set<string>();
  let reach = 0;
  const shapes = entries.map((entry) => {
    const isCover = entry.kind === "frame" && cover.has(frameKey((entry.pieces[0] as ScatterPiece).frame));
    return [0, 1, 2, 3].map((variant) => {
      if (!allows(entry.flip, variant)) {
        return undefined;
      }
      const shape = shapeOf(entry, (variant & 1) !== 0, (variant & 2) !== 0, gaps, tileSize, cellsPerTile, isCover);
      for (let i = 0; i < shape.pieces.length; i += 6) {
        reach = Math.max(reach, shape.footX - (shape.pieces[i] as number), (shape.pieces[i + 2] as number) - shape.footX, shape.footY - (shape.pieces[i + 1] as number), (shape.pieces[i + 3] as number) - shape.footY);
      }
      return shape;
    });
  });
  const cumulative: number[] = [];
  let total = 0;
  for (const entry of entries) {
    total += entry.weight;
    cumulative.push(total);
  }
  return { entries, shapes, cumulative, totalWeight: total, meanGapSq: meanGapSquared(entries, cellPx), maxGap: Math.max(...entries.map((e) => e.gap)), gaps, tileSize, cellsPerTile, cellPx, reach: reach + cellPx, ...(ground === undefined ? {} : { ground }), keepsGapFromOthers: options.keepsGapFromOthers === true, allowsPlacingOnTop: options.allowsPlacingOnTop === true, clearsGroundCover: options.clearsGroundCover === true && options.allowsPlacingOnTop !== true, cover };
}
